import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { CaseId, IphoneSmsConversation, SourceId } from '@casetrace/shared';
import {
  createIphoneSmsUpload,
  previewIphoneSms,
  startIphoneSmsImport,
  uploadSourceFile,
} from '../../../lib/callables';

export type IphoneSmsImportSelection = {
  readonly conversationIds: string[];
  readonly dateFrom?: string;
  readonly dateTo?: string;
};

export function useIphoneSmsImport(caseId: CaseId) {
  const [sourceId, setSourceId] = useState<SourceId | null>(null);
  const [conversations, setConversations] = useState<IphoneSmsConversation[]>([]);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!file.name.toLowerCase().endsWith('.db')) {
        throw new Error('Velg en .db-fil (sms.db fra iPhone-backup).');
      }
      const upload = await createIphoneSmsUpload({
        caseId,
        navn: 'SMS/iMessage',
        fileName: file.name,
      });
      await uploadSourceFile({
        caseId,
        sourceId: upload.sourceId,
        uploadObjectPath: upload.uploadObjectPath,
        file,
        contentType: upload.uploadContentType,
      });
      const preview = await previewIphoneSms({ caseId, sourceId: upload.sourceId });
      setSourceId(upload.sourceId);
      setConversations(preview.conversations);
      return preview;
    },
  });

  const importMutation = useMutation({
    mutationFn: async (selection: IphoneSmsImportSelection) => {
      if (!sourceId) {
        throw new Error('Mangler kilde. Last opp filen på nytt.');
      }
      return startIphoneSmsImport({
        caseId,
        sourceId,
        conversationIds: selection.conversationIds,
        dateFrom: selection.dateFrom,
        dateTo: selection.dateTo,
      });
    },
  });

  const reset = useCallback(() => {
    setSourceId(null);
    setConversations([]);
    uploadMutation.reset();
    importMutation.reset();
  }, [uploadMutation, importMutation]);

  return {
    sourceId,
    conversations,
    uploadMutation,
    importMutation,
    reset,
  };
}
